import * as React from 'react';
import { toast } from 'react-toastify';
import * as DateHelpers from './DateHelpers';
import type { NewJobQuote, NewJobQuoteItem, JobQuoteExtraItemOption, NewJobQuoteFenceQuestions, EpaQuestions, PowerPoleQuestions, FencingStock } from './models/JobInterfaces';
import type { ItemOptionList, GenericList } from './models/JobSharedInterfaces';

import NewJobPricelistEwsData from './NewJobPricelistEws';
import NewJobPricelistCameraData from './NewJobPricelistCamera';
import NewJobPricelistPowerPoleData from './NewJobPricelistPowerPole';
import NewJobPricelistToiletData from './NewJobPricelistToilet';
import NewJobPricelistFencingData from './NewJobPricelistFencing';
import NewJobPricelistOtherData from './NewJobPricelistOther';
import NewJobPricelistEventData from './NewJobPricelistEvent';
import NewJobPricelistEpaData from './NewJobPricelistEpa';
import { GetQuery } from './Shared';

interface NewJobPricelistProps {
    currentStep: number;
    priceOnly: boolean;
    jobTypeId: number;
    isEvent: boolean;
    isThreeDayHire: boolean;
    useTyabbPricelist: boolean;
    itemOptions: ItemOptionList[];
    extraItemOptions: JobQuoteExtraItemOption[];
    frequencyOptions: GenericList[];
    truckOptions: GenericList[];
    truckSideOptions: GenericList[];
    yesNoOptions: GenericList[];
    superSafeSupplierOptions: GenericList[];
    powerPoleLocationOptions: GenericList[];
    powerPoleDistanceOptions: GenericList[];
    epaExtra30MinOnSiteFeeFormatted: string;
    weekendFeeFormatted: string;
    emergencyFeeFormatted: string;
    truckHireRate: number;
    gateCode: string;
    allowAllocateToPastDate: boolean;
    lunchRoomExtraId: number;
    siteOfficeExtraId: number;
    customerId: number;
    accountTypeId: number;
    customerQuoteNotesDunny: string;
    customerInvoiceQuotedAmount: boolean;
    customerChargeFuelSurcharge: boolean;
    customerNewFuelInvoice: boolean;
    chargeFuelSurchargeSwitchedOn: boolean;
    siteAddressDuration: number;
    notes: string;
    quote: NewJobQuote;
    files: File[];
    goPrev(): void;
    updateNotes(notes: string): void;
    updateGateCode(gateCode: string): void;
    update(quote: NewJobQuote): void;
    updateFiles(files: File[], type: number): void; //type 1: add files, type 2: replace files
    goNext(): void;
}

interface NewJobPricelist {
    tab: string;
    useCustomerContact: boolean;
    isRabPthPricelist: boolean;
    showCustomPrices: boolean;
    customPrices: CustomPrices[];
    loading: boolean;
    errors: any;
}

interface CustomPrices {
    item: string;
    maintenance: string;
    metresMin: number;
    metresMax: number;
    metres: string;
    hirePeriod: number;
    priceFormatted: string;
    metreRateFormatted: string;
    travelOneWayRateFormatted: string;
    baseAdditionalHireRateFormatted: string;
    additionalHireRateFormatted: string;
}

export default class JobPricelist extends React.Component<NewJobPricelistProps, NewJobPricelist> {
    constructor(props: NewJobPricelistProps) {
        super(props);

        this.state = {
            tab: "T",
            useCustomerContact: false,
            isRabPthPricelist: false,
            showCustomPrices: false,
            customPrices: [],
            loading: true,
            errors: {}
        }
    }

    componentDidMount() {
        this.getData();
    }

    getData = async () => {
        const apiUrl = 'GetCustomPrices?CustomerId=' + this.props.customerId;
        var result = await GetQuery(apiUrl);

        if (result.error) {
            toast.error("Error getting data!");
        } else {
            var hasCustomPrices = result.data.customPrices.length > 0;
            this.setState({ isRabPthPricelist: result.data.isRabPthPricelist, customPrices: result.data.customPrices, showCustomPrices: hasCustomPrices, loading: false });
        }
    }

    tabChanged = () => {
        //nothing at this stage as you can't change tabs yet
    }

    tabSelected = (tab: string) => {
        this.setState({ tab: tab });
    }

    handleCustomPricesChange = (e: any) => {
        e.preventDefault();
        var currentShow = this.state.showCustomPrices;
        this.setState({ showCustomPrices: !currentShow });
    }

    handleChange = (e: any) => {
        let quote: any = {};
        quote = this.props.quote;
        quote[e.target.name] = e.target.value;
        this.props.update(quote);
    }

    handleCheckboxChange = (e: any) => {
        let quote: any = {};
        quote = this.props.quote;
        quote[e.target.name] = e.target.checked;

        if (e.target.name == "jobInstalled") {
            quote["jobInvoiced"] = false;   //reset to false whenever the job installed tickbox changes
            quote["jobInvoicedDate"] = "";

            //reset any fencing stock as this is only entered when the job has been already installed on site
            var quoteItems = [... quote.items];
            var fencingItems = quoteItems.filter(f => f.itemTypeId === 50);
            for (var i = 0; i < fencingItems.length; i++) {
                var fencingItem = fencingItems[i];
                var stock: FencingStock = fencingItem.fencingStock;
                stock.panels = 0;
                stock.blocks = 0;
                stock.clamps = 0;
                stock.pickets = 0;
                stock.signs = 0;
                stock.braces = 0;
            };
        }

        this.props.update(quote);
    }

    handleCustomerPickupChange = (e: any) => {
        let quote: any = {};
        quote = this.props.quote;
        quote[e.target.name] = e.target.value;
        this.props.update(quote);

        //re-calculate any quote items
        for (var i = 0; i < quote.items.length; i++) {
            var quoteItem = quote.items[i];
            this.calculatePrice(quote, quoteItem);
        }
    }

    handleRadioChange = (e: any) => {
        let quote: any = {};
        quote = this.props.quote;
        quote[e.target.name] = e.target.value * 1;
        this.props.update(quote);
    }

    async calculatePrice(quote: NewJobQuote, quoteItem: NewJobQuoteItem) {
        const baseURL = window.location.origin;

        //EWS units MUST have truck selected to calculate the price correctly
        let canCalc = (quoteItem.isEws && quoteItem.isWaste) || (quoteItem.isEws && quoteItem.truck > 0) || !quoteItem.isEws;

        //calculate totals
        if (canCalc && quoteItem.itemId > 0 && quoteItem.hirePeriod > 0 && quoteItem.quantity > 0) {
            var itemOption = this.props.itemOptions.find(i => i.id === quoteItem.itemId);
            var chargeGst = itemOption?.chargeGst;
            var extraItemId = quoteItem.extras.itemId < 0 ? 0 : quoteItem.extras.itemId;

            var powerPoleSupplierId = quote.powerPoleQuestions.superSafeSupplierId;
            var powerPoleDistanceId = quote.powerPoleQuestions.pitDistanceId;
            var powerPoleSsDiggingTrench = quote.powerPoleQuestions.superSafeDiggingTrench === 1;

            var chargeTravel = quote.customerPickingUp < 1;       //default to true unless customer is picking up (1)
            if (quoteItem.itemTypeId === 27 || quoteItem.itemTypeId == 28 || quoteItem.itemTypeId === 40) {
                //crossovers, rubbish cages and urinals don't have a travel charge if their is a toilet being hired
                var hiringToilet = quote.items.filter(i => i.itemTypeId === 25 || i.itemTypeId === 33).length > 0;
                if (hiringToilet) {
                    //toilet is being hired
                    chargeTravel = false;
                }
            }
            if (quoteItem.itemTypeId === 53) {
                //silt barriers are charged travel if no fencing hired
                var hiringFence = quote.items.filter(i => i.itemTypeId === 50).length > 0;
                if (hiringFence) {
                    chargeTravel = false;
                }
            }

            var hirePeriod = quoteItem.customHirePeriod && quoteItem.invoicePeriodTypeId === 1 ? (quoteItem.hirePeriod * 7) : quoteItem.hirePeriod;
            let totalSteps = 0;
            let stepItems = quote.items.filter(i => i.itemTypeId === 55);
            if (quoteItem.itemTypeId === 55) {
                //need to sum up all the steps quantity to calculate the travel
                for (var i = 0; i < stepItems.length; i++) {
                    totalSteps += stepItems[i].quantity;
                }
            }
            let weekendJob = quote.epaQuestions.weekendJob === 1;
            let emergencyJob = quote.epaQuestions.emergencySameDayJob === 1;
            let chargeFuelSurcharge = this.props.chargeFuelSurchargeSwitchedOn && this.props.customerChargeFuelSurcharge;   //customer must be set to charge fuel and fuel charge needs to be on

            const apiUrl = 'CalculateNewJobPrice?JobId=0&CustomerId=' + this.props.customerId + "&Duration=" + this.props.siteAddressDuration + "&ItemId=" + quoteItem.itemId + "&JobItemTypeId=" + quoteItem.itemTypeId + "&ProductGroupId=" + quoteItem.productGroupId + "&IsEws=" + itemOption?.isEws + "&IsEvent=" + quoteItem.isEvent + "&HirePeriod=" + hirePeriod + "&UseTyabbPricelist=" + this.props.useTyabbPricelist + "&UsePerMetrePricing=false&FrequencyId=" + quoteItem.frequencyId + "&LocationId=" + quoteItem.locationId + "&Size=" + quoteItem.size + "&ExtraHours=" + quoteItem.hours + "&Quantity=" + quoteItem.quantity + "&ChargeGst=" + chargeGst + "&TruckExtraHours=" + quoteItem.truck + "&ExtraItemId=" + extraItemId + "&ExtraItemQuantity=" + quoteItem.extras.quantity + "&IncludeTrolley=" + quoteItem.trolley + "&ChargeTravel=" + chargeTravel + "&PowerPoleSupplierId=" + powerPoleSupplierId + "&PowerPoleDistanceId=" + powerPoleDistanceId + "&PowerPoleSSDiggingTrench=" + powerPoleSsDiggingTrench + "&TotalSteps=" + totalSteps + "&IncludeStepsTravel=" + quoteItem.incStepsTravel + "&ChargeWeekendJob=" + weekendJob + "&ChargeEmergencyJob=" + emergencyJob + "&ChargeFuelSurcharge=" + chargeFuelSurcharge;
            var result = await GetQuery(apiUrl);

            if (result.error) {
                toast.error("Error getting data!");
            } else {
                quoteItem.initialHire = result.data.initialHire;
                quoteItem.trench = result.data.trenchFee;
                quoteItem.cleaning = result.data.cleaning;
                quoteItem.travel = result.data.travel;
                quoteItem.fuelSurcharge = result.data.fuelSurcharge;
                quoteItem.amount = result.data.amount;
                quoteItem.overhireRate = result.data.overhireRate;
                quoteItem.calculatedPriceNote = result.data.calculatedPriceNote;

                //if we are modifying a steps item then we may need to update the travel cost on another step item
                if (quoteItem.itemTypeId === 55 && !quoteItem.incStepsTravel) {
                    //update other steps item
                    //see if there is one that currently includes travel
                    var stepItemIncTravel = stepItems.find(s => s.incStepsTravel);
                    if (stepItemIncTravel) {
                        stepItemIncTravel.travel = result.data.stepsTravelAmount;
                        //calculate Total Amount
                        stepItemIncTravel.amount = stepItemIncTravel.initialHire + stepItemIncTravel.travel;
                    } else {
                        //include travel in this item
                        quoteItem.travel = result.data.stepsTravelAmount;
                        quoteItem.amount = quoteItem.initialHire + quoteItem.travel;
                    }
                }
                this.props.update(quote);
            }
        } else {
            this.props.update(quote);
        }
    }

    noChange = (_e: any) => {

    }

    handleImportFile = (e: any) => {
        this.props.updateFiles(e.target.files, 1);       
    }

    deleteFile = (index: number) => {
        var jobFiles = [...this.props.files];
        jobFiles.splice(index, 1);
        this.props.updateFiles(jobFiles, 2);
    }

    addItem = (quoteItem: NewJobQuoteItem, calculatePrice: boolean) => {
        let quote = this.props.quote;
        let quoteItems = quote.items;

        quoteItem.itemIndex = quoteItems.length;

        quoteItems.push(quoteItem);

        if (calculatePrice) {
            this.calculatePrice(this.props.quote, quoteItem);
        } else {
            this.props.update(quote);
        }
    }

    deleteItem = (itemIndex: number) => {
        let quote = this.props.quote;
        let quoteItems = quote.items;
        var deletedItems = quoteItems.splice(itemIndex, 1); //will only be one but returns a list

        //redo the item indexes
        for (var i = 0; i < quoteItems.length; i++) {
            var quoteItem = quoteItems[i];
            quoteItem.itemIndex = i;
        }

        if (deletedItems.length > 0) {
            var deletedItem = deletedItems[0];
            if (deletedItem.itemTypeId === 55) {
                //deleted a steps item which means we need to re-calculate travel for any exisiting steps items
                var stepItems = quoteItems.filter(q => q.itemTypeId === 55);
                if (stepItems.length > 0) {
                    var stepItemIncTravel = stepItems.find(s => s.incStepsTravel);
                    if (stepItemIncTravel) {
                        //need to recalc travel for this item
                        this.calculatePrice(quote, stepItemIncTravel);
                    } else {
                        //pick first step item to include travel
                        var stepItem = stepItems[0];
                        stepItem.incStepsTravel = true;
                        this.calculatePrice(quote, stepItem);
                    }
                } else {
                    this.props.update(quote);
                }
            } else if (deletedItem.itemTypeId >= 58 && deletedItem.itemTypeId <= 61) {
                //clear epa questions
                var epaQuestions = this.resetEpaQuestions();
                quote.epaQuestions = epaQuestions;
                this.props.update(quote);
            } else {
                this.props.update(quote);
            }
        } else {
            this.props.update(quote);
        }
    }

    resetEpaQuestions = () => {
        var epaQuestions: EpaQuestions = {
            tankLidExposed: -1,
            tankLidLocationKnown: -1,
            tankLidLocation: "",
            truckLargeAccess: -1,
            truckSmallAccess: -1,
            truckAccessToTank: -1,  //within 25m
            steepDriveway: -1,
            hoseMetres: 0,
            sealedDriveway: -1,
            lastService: "",
            waterBoard: "",
            waterAccessOnSite: -1,
            emergencySameDayJob: -1,
            weekendJob: -1,
            siteVisit: -1,
            confinedSpace: -1,
            materialSafetyDataSheet: -1,
            sampleTestingRequired: -1
        };

        return epaQuestions;
    }

    addBlankQuoteItem = () => {
        var item: NewJobQuoteItem = {
            itemIndex: -1,
            itemId: -1,
            item: "",
            itemTypeId: -1,
            productGroupId: 0,
            itemDescription: "",
            installOnly: false,
            extendable: true,
            isEws: false,
            isWaste: false,
            isUnderground: false,
            isEvent: false,
            unitNumber: "",
            scheduleDate: "",
            onHireDate: "",
            offhireDate: "",
            pickupDate: "",
            hirePeriod: -1,
            defaultHirePeriod: 42,
            customHirePeriod: false,
            frequencyId: -1,
            locationId: -1,
            fenceBraceId: -1,
            hours: 0,
            size: 0,
            quantity: 1,
            unitOfMeasure: "",
            initialHire: 0,
            travel: 0,
            cleaning: 0,
            trench: 0,
            confinedSpace: 0,
            fuelSurcharge: 0,
            amount: 0,
            overhireRate: "0.00",
            truck: 0,
            truckDoors: -1,
            purchaseOrderNumber: "",
            purchaseOrderNumberFuel: "",
            notes: "",
            trolley: false,
            extras: {
                itemId: -1,
                quantity: 0,
                equipped: false
            },
            fencingStock: {
                panels: 0,
                blocks: 0,
                clamps: 0,
                pickets: 0,
                signs: 0,
                braces: 0
            },
            calculatedPriceNote: "",
            deliveryAddress: "",
            deliveryAddressGooglePlaceId: "",
            deliveryAddressLatitude: 0,
            deliveryAddressLongitude: 0,
            invoiceDescription: "",
            incStepsTravel: false,
            invoicePeriodTypeId: 1
        };

        return item;
    }

    updateQuoteItem = (quoteItem: NewJobQuoteItem, calculatePrice: boolean) => {
        let quote = this.props.quote;
        let quoteItems = quote.items;
        let item = quoteItems[quoteItem.itemIndex];

        if (item.truck != quoteItem.truck) {
            quote.tiltSlideClearance = -1;
        }

        //replace item with updated version
        item = quoteItem;

        if (calculatePrice) {
            this.calculatePrice(this.props.quote, item);
        } else {
            this.props.update(quote);
        }
    }

    updateTiltClearance = (tiltSlideClearance: number) => {
        let quote = this.props.quote;
        quote.tiltSlideClearance = tiltSlideClearance;
        this.props.update(quote);
    }

    updateCameraLocation = (cameraLocation: string) => {
        let quote = this.props.quote;
        quote.cameraLocation = cameraLocation;
        this.props.update(quote);
    }

    updatePowerPoleQuestions = (ppQuestions: PowerPoleQuestions, calculatePrice: boolean) => {
        let quote = this.props.quote;
        quote.powerPoleQuestions = ppQuestions;

        if (calculatePrice) {
            var ppItems = quote.items.filter(i => i.itemTypeId === 23);
            if (ppItems.length > 0) {
                let item = ppItems[0];
                this.calculatePrice(this.props.quote, item);
            }
        } else {
            this.props.update(quote);
        }
    }

    updateFenceQuestions = (fenceQuestions: NewJobQuoteFenceQuestions) => {
        let quote = this.props.quote;
        quote.fenceQuestions = fenceQuestions;
        this.props.update(quote);
    }

    updateEpaQuestions = (epaQuestions: EpaQuestions) => {
        let quote = this.props.quote;
        quote.epaQuestions = epaQuestions;
        this.props.update(quote); 
    }

    addError = (errorName: string, errorMessage: string) => {
        var errorList = this.state.errors;
        errorList[errorName] = errorMessage;
        this.setState({ errors: errorList });
    } 

    clearError = (errorName: string) => {
        var errorList = this.state.errors;
        if (errorName) {
            delete errorList[errorName];
            this.setState({ errors: errorList });
        }
    }

    prev = () => {
        this.props.goPrev();
    }

    validate = () => {
        let quote = this.props.quote;
        let fields = quote.items;
        let errors: any = {};
        let formIsValid = true;
        var errorItems: string[] = [];

        if (fields.length <= 1) {
            formIsValid = false;
            errors["quoteItems"] = "At least one Item is required!";
        }

        if (quote.customerPickingUp < 0) {
            formIsValid = false;
            errors["customerPickingUp"] = "Customer Picking Up is required";
        }

        //don't get payment or set site contact when creating a pricelist quote
        if (this.props.jobTypeId != 3) {
            if (quote.paymentType < 1) {
                formIsValid = false;
                errors["paymentType"] = "Payment Type is required";
            }
        }

        var custPickup = quote.customerPickingUp * 1;

        //ews item validation
        let ewsFields = quote.items.filter(i => i.isEws);
        if (ewsFields.length > 0) {
            let isTiltSlide = ewsFields.filter(q => q.truck === 1).length > 0;
            let errorEws = false;

            if (isTiltSlide && quote.tiltSlideClearance < 0 && custPickup === 0) {
                formIsValid = false;
                errors["tiltSlideClearance"] = "Tilt Slide Clearance is required";
                errorItems.push("EWS");
                errorEws = true;
            }

            let items = ewsFields;
            let rowNumber = 0;
            let errorString = "Please enter the following values: ";
            let error = false;
            //last row is a blank new row so ignore
            for (var i = 0; i < items.length; i++) {
                rowNumber += 1;
                var newErrorString = "";
                if (items[i].itemId < 0) {
                    newErrorString += " Item, ";
                    error = true;
                }

                let isElite = items[i].item.toLowerCase().includes("elite");
                if (items[i].truck < 1 && !items[i].isWaste) {
                    newErrorString += " Truck, ";
                    error = true;
                } else if (isElite && items[i].truck != 1) {
                    newErrorString += " Elite Units MUST use Tilt Truck, ";
                    error = true;
                }

                //don't check truck doors or schedule date when doing a pricelist quote
                if (this.props.jobTypeId != 3) {
                    if (items[i].truckDoors < 1 && !items[i].isWaste && custPickup === 0) {
                        newErrorString += " Truck Doors Direction, ";
                        error = true;
                    }

                    if (!items[i].scheduleDate) {
                        newErrorString += " Valid Delivery Date, ";
                        error = true;
                    } else if (!DateHelpers.isValidDate(items[i].scheduleDate)) {
                        newErrorString += " Valid Delivery Date, ";
                        error = true;
                    }
                    if ((this.props.isEvent || this.props.isThreeDayHire || this.props.quote.schedulePickup) && !items[i].pickupDate && !items[i].installOnly) {
                        newErrorString += " Valid Pickup Date, ";
                        error = true;
                    } else if (items[i].pickupDate && items[i].scheduleDate) {
                        if (!DateHelpers.isValidDate(items[i].pickupDate)) {
                            newErrorString += " Valid Pickup Date, ";
                            error = true;
                        }
                        else if (!DateHelpers.compareDate(items[i].scheduleDate!, items[i].pickupDate!)) {
                            newErrorString += " Pickup Date >= Schedule Date, ";
                            error = true;
                        }
                    }
                }

                if (items[i].hirePeriod <= 0) {
                    newErrorString += " Hire Period, ";
                    error = true;
                }

                if (items[i].quantity <= 0) {
                    newErrorString += " Quantity, ";
                    error = true;
                }

                var initialHire = items[i].initialHire * 1;
                if (initialHire <= 0) {
                    newErrorString += " Initial Hire, ";
                    error = true;
                }

                var amount = items[i].amount * 1;
                if (amount <= 0) {
                    newErrorString += " Amount, ";
                    error = true;
                }

                var overhireRate = parseFloat(items[i].overhireRate);
                if (overhireRate <= 0) {
                    newErrorString += " Overhire Rate, ";
                    error = true;
                }

                if (newErrorString != "") {
                    errorString += "\n"
                    errorString += "Row " + rowNumber + ": " + newErrorString;
                    errorString = errorString.substring(0, errorString.length - 2);
                }

            }

            if (error) {
                formIsValid = false;
                errors["ewsQuoteItems"] = errorString;
                if (!errorEws) {
                    errorItems.push("EWS");
                }
            }
        }

        //camera validation
        let cameraFields = quote.items.filter(i => i.itemTypeId === 19);
        if (cameraFields.length > 0) {
            let errorCamera = false;

            if (!quote.cameraLocation) {
                formIsValid = false;
                errors["cameraLocation"] = "Camera Location is required";
                errorCamera = true;
                errorItems.push("Cameras");
            }

            let rowCamNumber = 0;
            let camErrorString = "Please enter the following values: ";
            let errorCam = false;
            //last row is a blank new row so ignore
            for (var i = 0; i < cameraFields.length; i++) {
                rowCamNumber += 1;
                let newCamErrorString = "";

                if (cameraFields[i].itemId < 0) {
                    newCamErrorString += " Item, ";
                    errorCam = true;
                }

                //don't schedule jobs for pricelist quotes
                if (this.props.jobTypeId != 3) {
                    if (!cameraFields[i].scheduleDate) {
                        newCamErrorString += " Valid Delivery Date, ";
                        errorCam = true;
                    } else if (!DateHelpers.isValidDate(cameraFields[i].scheduleDate)) {
                        newCamErrorString += " Valid Delivery Date, ";
                        errorCam = true;
                    }
                    if ((this.props.isEvent || this.props.isThreeDayHire || this.props.quote.schedulePickup) && !cameraFields[i].pickupDate && !cameraFields[i].installOnly) {
                        newCamErrorString += " Valid Pickup Date, ";
                        errorCam = true;
                    } else if (cameraFields[i].pickupDate && cameraFields[i].scheduleDate) {
                        if (!DateHelpers.isValidDate(cameraFields[i].pickupDate)) {
                            newCamErrorString += " Valid Pickup Date, ";
                            errorCam = true;
                        }
                        else if (!DateHelpers.compareDate(cameraFields[i].scheduleDate!, cameraFields[i].pickupDate!)) {
                            newCamErrorString += " Pickup Date >= Schedule Date, ";
                            errorCam = true;
                        }
                    }
                }

                if (cameraFields[i].hirePeriod <= 0) {
                    newCamErrorString += " Hire Period, ";
                    errorCam = true;
                }

                if (cameraFields[i].quantity <= 0) {
                    newCamErrorString += " Quantity, ";
                    errorCam = true;
                }

                var initialHire = cameraFields[i].initialHire * 1;
                if (initialHire <= 0) {
                    newCamErrorString += " Initial Hire, ";
                    errorCam = true;
                }

                var amount = cameraFields[i].amount * 1;
                if (amount <= 0) {
                    newCamErrorString += " Amount, ";
                    errorCam = true;
                }

                var overhireRate = parseFloat(cameraFields[i].overhireRate);
                if (overhireRate <= 0) {
                    newCamErrorString += " Overhire Rate, ";
                    errorCam = true;
                }

                if (newCamErrorString != "") {
                    camErrorString += "\n"
                    camErrorString += "Row " + rowCamNumber + ": " + newCamErrorString;
                    camErrorString = camErrorString.substring(0, camErrorString.length - 2);
                }

            }

            if (errorCam) {
                formIsValid = false;
                errors["cameraQuoteItems"] = camErrorString;
                if (!errorCamera) {
                    errorItems.push("Cameras");
                }
            }
        }

        //power pole validation
        let powerPoleFields = quote.items.filter(i => i.itemTypeId === 23 || i.itemTypeId === 57);
        if (powerPoleFields.length > 0) {
            //can only have one power pole so get first one
            var powerPoleItem = powerPoleFields[0];

            let errorPowerPole = false;

            if (powerPoleItem.itemTypeId === 23) {
                if (quote.powerPoleQuestions.superSafeConnecting < 0) {
                    formIsValid = false;
                    errors["superSafeConnecting"] = "Super Safe Connecting Electricity is required";
                    errorPowerPole = true;
                }

                if (quote.powerPoleQuestions.superSafeConnecting === 1 && quote.powerPoleQuestions.superSafeSupplierId < 0) {
                    formIsValid = false;
                    errors["superSafeSupplierId"] = "Distributor is required";
                    errorPowerPole = true;
                }

                if (quote.powerPoleQuestions.powerPoleLocationId < 0) {
                    formIsValid = false;
                    errors["powerPoleLocationId"] = "Power Pole Location is required";
                    errorPowerPole = true;
                }

                if (quote.powerPoleQuestions.powerPreviouslyConnected < 0) {
                    formIsValid = false;
                    errors["powerPreviouslyConnected"] = "Power to the property is required";
                    errorPowerPole = true;
                }

                if (quote.powerPoleQuestions.powerPreviouslyConnected === 1 && quote.powerPoleQuestions.powerAbolished < 0) {
                    formIsValid = false;
                    errors["powerAbolished"] = "Power Abolished is required";
                    errorPowerPole = true;
                }

                if (quote.powerPoleQuestions.siteLocked < 0) {
                    formIsValid = false;
                    errors["siteLocked"] = "Is Site Locked is required";
                    errorPowerPole = true;
                }

                if (!powerPoleItem.isUnderground && quote.powerPoleQuestions.clearSightLine < 0) {
                    formIsValid = false;
                    errors["clearSightLine"] = "Clear Line of Sight is required";
                    errorPowerPole = true;
                }

                if (powerPoleItem.isUnderground) {
                    if (quote.powerPoleQuestions.pitOnSite < 0) {
                        formIsValid = false;
                        errors["pitOnSite"] = "Pit on site is required";
                        errorPowerPole = true;
                    } else if (quote.powerPoleQuestions.pitOnSite === 1 && quote.powerPoleQuestions.pitDistanceId < 0) {
                        formIsValid = false;
                        errors["pitOnSite"] = "Distance between pit and pole is required";
                        errorPowerPole = true;
                    }
                }

                if (errorPowerPole) {
                    errorItems.push("PowerPoles");
                }
            }
            //Check Power Pole Item details
            let ppErrorString = "Please enter the following values: ";
            let errorPp = false;

            if (powerPoleItem.itemId < 0) {
                ppErrorString += " Item, ";
                errorPp = true;

            }

            //don't schedule jobs for pricelist quotes
            if (this.props.jobTypeId != 3) {
                if (!powerPoleItem.scheduleDate) {
                    ppErrorString += " Valid Delivery Date, ";
                    errorPp = true;
                } else if (!DateHelpers.isValidDate(powerPoleItem.scheduleDate)) {
                    ppErrorString += " Valid Delivery Date, ";
                    errorPp = true;
                }
                if ((this.props.isEvent || this.props.isThreeDayHire || this.props.quote.schedulePickup) && !powerPoleItem.pickupDate && !powerPoleItem.installOnly) {
                    ppErrorString += " Valid Pickup Date, ";
                    errorPp = true;
                } else if (powerPoleItem.pickupDate && powerPoleItem.scheduleDate) {
                    if (!DateHelpers.isValidDate(powerPoleItem.pickupDate)) {
                        ppErrorString += " Valid Pickup Date, ";
                        errorPp = true;
                    }
                    else if (!DateHelpers.compareDate(powerPoleItem.scheduleDate!, powerPoleItem.pickupDate!)) {
                        ppErrorString += " Pickup Date >= Schedule Date, ";
                        errorPp = true;
                    }
                }
            }

            if (powerPoleItem.hirePeriod <= 0) {
                ppErrorString += " Hire Period, ";
                errorPp = true;
            }

            if (powerPoleItem.quantity <= 0) {
                ppErrorString += " Quantity, ";
                errorPp = true;
            }

            var initialHire = powerPoleItem.initialHire * 1;
            if (initialHire <= 0) {
                ppErrorString += " Initial Hire, ";
                errorPp = true;
            }

            var amount = powerPoleItem.amount * 1;
            if (amount <= 0) {
                ppErrorString += " Amount, ";
                errorPp = true;
            }

            if (powerPoleItem.itemTypeId != 57) {
                var overhireRate = parseFloat(powerPoleItem.overhireRate);
                if (overhireRate <= 0) {
                    ppErrorString += " Overhire Rate, ";
                    errorPp = true;
                }
            }

            if (ppErrorString != "") {
                ppErrorString = ppErrorString.substring(0, ppErrorString.length - 2);
            }

            if (errorPp) {
                formIsValid = false;
                errors["powerPoleQuoteItems"] = ppErrorString;
                if (!errorPowerPole) {
                    errorItems.push("PowerPoles");
                }
            }
        }

        //fencing validation
        let fencingFields = quote.items.filter(i => i.itemTypeId === 8 || (i.itemTypeId >= 50 && i.itemTypeId <= 54));
        let fencingQuestionFields = quote.items.filter(i => i.itemTypeId === 50 || i.itemTypeId === 51 || i.itemTypeId === 54);
        if (fencingFields.length > 0) {
            let errorFencing = false;   //used for fencing questions

            if (fencingQuestionFields.length > 0) {
                //only need to answer these questions for fencing standard and braces
                var isFenceAdded = fencingQuestionFields.filter(f => f.itemTypeId === 50).length > 0;
                if (quote.fenceQuestions.fenceTotalMetres <= 0 && isFenceAdded) {
                    formIsValid = false;
                    errors["fenceTotalMetres"] = "Fence Total Metres is required";
                    errorFencing = true;
                }

                if (quote.fenceQuestions.signageAdded && quote.fenceQuestions.signagePanels <= 0) {
                    formIsValid = false;
                    errors["signagePanels"] = "# Panels with Signs is required";
                    errorFencing = true;
                }

                var fenceAreas = quote.fenceQuestions.fenceAreas.filter(f => f.selected);
                if (fenceAreas.length === 0) {
                    formIsValid = false;
                    errors["fenceArea"] = "Fence Area is required";
                    errorFencing = true;
                }
            }

            if (errorFencing) {
                errorItems.push("Fencing");
            }

            let rowFencingNumber = 0;
            let fencingErrorString = "Please enter the following values: ";
            let errorFence = false;

            for (var i = 0; i < fencingFields.length; i++) {
                rowFencingNumber += 1;
                let newFenceErrorString = "";

                if (fencingFields[i].itemId < 0) {
                    newFenceErrorString += " Item, ";
                    errorFence = true;
                }

                if (quote.jobInstalled && fencingFields[i].itemTypeId === 50 && fencingFields[i].fencingStock.panels < 1) {
                    //when a job has already been installed on site they need to enter stock
                    newFenceErrorString += "Fencing Stock (via + popup), ";
                    errorFence = true;
                }

                if (fencingFields[i].itemTypeId === 51 && fencingFields[i].fenceBraceId < 0) {
                    newFenceErrorString += " Bracing, ";
                    errorFence = true;
                }

                if (this.props.jobTypeId != 3) {
                    if (!fencingFields[i].scheduleDate) {
                        newFenceErrorString += " Valid Delivery Date, ";
                        errorFence = true;
                    } else if (!DateHelpers.isValidDate(fencingFields[i].scheduleDate)) {
                        newFenceErrorString += " Valid Delivery Date, ";
                        errorFence = true;
                    }

                    if ((this.props.isEvent || this.props.isThreeDayHire || this.props.quote.schedulePickup) && !fencingFields[i].pickupDate && !fencingFields[i].installOnly) {
                        newFenceErrorString += " Valid Pickup Date, ";
                        errorFence = true;
                    } else if (fencingFields[i].pickupDate && fencingFields[i].scheduleDate) {
                        if (!DateHelpers.isValidDate(fencingFields[i].pickupDate)) {
                            newFenceErrorString += " Valid Pickup Date, ";
                            errorFence = true;
                        }
                        else if (!DateHelpers.compareDate(fencingFields[i].scheduleDate!, fencingFields[i].pickupDate!)) {
                            newFenceErrorString += " Pickup Date >= Schedule Date, ";
                            errorFence = true;
                        }
                    }
                }

                if (fencingFields[i].hirePeriod <= 0) {
                    newFenceErrorString += " Hire Period, ";
                    errorFence = true;
                }

                if (fencingFields[i].quantity <= 0) {
                    newFenceErrorString += " Quantity, ";
                    errorFence = true;
                }

                var initialHire = fencingFields[i].initialHire * 1;
                if (initialHire <= 0) {
                    newFenceErrorString += " Initial Hire, ";
                    errorFence = true;
                }

                var amount = fencingFields[i].amount * 1;
                if (amount <= 0) {
                    newFenceErrorString += " Amount, ";
                    errorFence = true;
                }

                var overhireRate = parseFloat(fencingFields[i].overhireRate);
                if (overhireRate <= 0 && fencingFields[i].extendable) {
                    newFenceErrorString += " Overhire Rate, ";
                    errorFence = true;
                }

                if (newFenceErrorString != "") {
                    fencingErrorString += "\n"
                    fencingErrorString += "Row " + rowFencingNumber + ": " + newFenceErrorString;
                    fencingErrorString = fencingErrorString.substring(0, fencingErrorString.length - 2);
                }
            }

            if (errorFence) {
                formIsValid = false;
                errors["fencingQuoteItems"] = fencingErrorString;
                if (!errorFencing) {
                    errorItems.push("Fencing");
                }
            }
        }

        //toilet validation
        let toiletFields = quote.items.filter(i => i.itemTypeId === 25 || i.itemTypeId === 26 || (i.itemTypeId >= 44 && i.itemTypeId <= 47));
        if (toiletFields.length > 0) {
            let rowToiletNumber = 0;
            let toiletErrorString = "Please enter the following values: ";
            let errorToil = false;

            for (var i = 0; i < toiletFields.length; i++) {
                rowToiletNumber += 1;
                let newToilErrorString = "";

                if (toiletFields[i].itemId < 0) {
                    newToilErrorString += " Item, ";
                    errorToil = true;
                }

                //don't schedule jobs for pricelist quotes
                if (this.props.jobTypeId != 3) {
                    if (!toiletFields[i].scheduleDate) {
                        newToilErrorString += " Valid Delivery Date, ";
                        errorToil = true;
                    } else if (!DateHelpers.isValidDate(toiletFields[i].scheduleDate)) {
                        newToilErrorString += " Valid Delivery Date, ";
                        errorToil = true;
                    }
                    if ((this.props.isEvent || this.props.isThreeDayHire || this.props.quote.schedulePickup) && !toiletFields[i].pickupDate && !toiletFields[i].installOnly) {
                        newToilErrorString += " Valid Pickup Date, ";
                        errorToil = true;
                    } else if (toiletFields[i].pickupDate && toiletFields[i].scheduleDate) {
                        if (!DateHelpers.isValidDate(toiletFields[i].pickupDate)) {
                            newToilErrorString += " Valid Pickup Date, ";
                            errorToil = true;
                        }
                        else if (!DateHelpers.compareDate(toiletFields[i].scheduleDate!, toiletFields[i].pickupDate!)) {
                            newToilErrorString += " Pickup Date >= Schedule Date, ";
                            errorToil = true;
                        }
                    }
                }

                if (toiletFields[i].hirePeriod <= 0) {
                    newToilErrorString += " Hire Period, ";
                    errorToil = true;
                }

                if (toiletFields[i].quantity <= 0) {
                    newToilErrorString += " Quantity, ";
                    errorToil = true;
                }

                var initialHire = toiletFields[i].initialHire * 1;
                if (initialHire <= 0) {
                    newToilErrorString += " Initial Hire, ";
                    errorToil = true;
                }

                var amount = toiletFields[i].amount * 1;
                if (amount <= 0) {
                    newToilErrorString += " Amount, ";
                    errorToil = true;
                }

                var overhireRate = parseFloat(toiletFields[i].overhireRate);
                if (overhireRate <= 0 && toiletFields[i].extendable) {
                    newToilErrorString += " Overhire Rate, ";
                    errorToil = true;
                }

                if (newToilErrorString != "") {
                    toiletErrorString += "\n"
                    toiletErrorString += "Row " + rowToiletNumber + ": " + newToilErrorString;
                    toiletErrorString = toiletErrorString.substring(0, toiletErrorString.length - 2);
                }

            }

            if (errorToil) {
                formIsValid = false;
                errors["toiletQuoteItems"] = toiletErrorString;
                errorItems.push("Toilets");
            }
        }

        //epa validation
        let epaFields = quote.items.filter(i => i.itemTypeId >= 58 && i.itemTypeId <= 61);
        if (epaFields.length > 0) {
            let rowEpaNumber = 0;
            let epaErrorString = "Please enter the following values: ";
            let errorEpa = false;

            for (var i = 0; i < epaFields.length; i++) {
                rowEpaNumber += 1;
                let newEpaErrorString = "";

                if (epaFields[i].itemId < 0) {
                    newEpaErrorString += " Item, ";
                    errorEpa = true;
                }

                //don't schedule jobs for pricelist quotes
                if (this.props.jobTypeId != 3) {
                    if (!epaFields[i].scheduleDate) {
                        newEpaErrorString += " Valid Delivery Date, ";
                        errorEpa = true;
                    } else if (!DateHelpers.isValidDate(epaFields[i].scheduleDate)) {
                        newEpaErrorString += " Valid Delivery Date, ";
                        errorEpa = true;
                    }
                }

                if (epaFields[i].size <= 0) {
                    newEpaErrorString += " Size, ";
                    errorEpa = true;
                }

                //need truck hours for EPA Other items
                if (epaFields[i].itemTypeId === 61) {
                    if (epaFields[i].hours <= 0) {
                        newEpaErrorString += " Truck Hours, ";
                        errorEpa = true;
                    }
                }

                if (epaFields[i].quantity <= 0) {
                    newEpaErrorString += " Quantity, ";
                    errorEpa = true;
                }

                var initialHire = epaFields[i].initialHire * 1;
                if (initialHire <= 0) {
                    newEpaErrorString += " Initial Hire, ";
                    errorEpa = true;
                }

                var amount = epaFields[i].amount * 1;
                if (amount <= 0) {
                    newEpaErrorString += " Amount, ";
                    errorEpa = true;
                }

                if (newEpaErrorString != "") {
                    epaErrorString += "\n"
                    epaErrorString += "Row " + rowEpaNumber + ": " + newEpaErrorString;
                    epaErrorString = epaErrorString.substring(0, epaErrorString.length - 2);
                }

            }

            if (errorEpa) {
                formIsValid = false;
                errors["epaQuoteItems"] = epaErrorString;
                errorItems.push("Epa");
            }


            //check EPA questions
            let errorEpaQuestions = false;   //used for epa questions
            let isSepticTank = epaFields.filter(f => f.itemTypeId === 58).length > 0;
            let isGreaseTrap = epaFields.filter(f => f.itemTypeId === 59).length > 0;
            let isOther = epaFields.filter(f => f.itemTypeId === 61).length > 0;
            let canHaveMaint = epaFields.filter(f => f.itemTypeId != 61).length > 0;

            if (isSepticTank) {
                //only need to answer these questions for septic tanks
                
                if (quote.epaQuestions.tankLidExposed < 0) {
                    formIsValid = false;
                    errors["tankLidExposed"] = "Lid of Tank Exposed is required";
                    errorEpaQuestions = true;
                }

                if (quote.epaQuestions.tankLidExposed === 0 && quote.epaQuestions.tankLidLocationKnown < 0) {
                    formIsValid = false;
                    errors["tankLidLocationKnown"] = "Where tank lid is located is required";
                    errorEpaQuestions = true;
                }

                if (quote.epaQuestions.tankLidLocationKnown === 1 && quote.epaQuestions.tankLidLocation == "") {
                    formIsValid = false;
                    errors["tankLidLocation"] = "Tank lid is located is required";
                    errorEpaQuestions = true;
                }

                if (quote.epaQuestions.truckLargeAccess < 0) {
                    formIsValid = false;
                    errors["truckLargeAccess"] = "Access for large truck is required";
                    errorEpaQuestions = true;
                }

                if (quote.epaQuestions.truckLargeAccess === 0 && quote.epaQuestions.truckSmallAccess < 0) {
                    formIsValid = false;
                    errors["truckSmallAccess"] = "Access for small truck is required";
                    errorEpaQuestions = true;
                }

                if (quote.epaQuestions.truckAccessToTank < 0) {
                    formIsValid = false;
                    errors["truckAccessToTank"] = "Truck access within 25m is required";
                    errorEpaQuestions = true;
                }

                if (quote.epaQuestions.steepDriveway < 0) {
                    formIsValid = false;
                    errors["steepDriveway"] = "Steep driveway is required";
                    errorEpaQuestions = true;
                }

                if ((quote.epaQuestions.truckAccessToTank === 0 || quote.epaQuestions.steepDriveway === 1) && quote.epaQuestions.hoseMetres <= 0 ) {
                    formIsValid = false;
                    errors["hoseMetres"] = "# Hose Metres is required";
                    errorEpaQuestions = true;
                }

                if (quote.epaQuestions.sealedDriveway < 0) {
                    formIsValid = false;
                    errors["sealedDriveway"] = "Sealed driveway is required";
                    errorEpaQuestions = true;
                }
            }

            if (isOther) {
                if (quote.epaQuestions.siteVisit < 0) {
                    formIsValid = false;
                    errors["siteVisit"] = "Site Visit is required";
                    errorEpaQuestions = true;
                }
                if (quote.epaQuestions.confinedSpace < 0) {
                    formIsValid = false;
                    errors["confinedSpace"] = "Confined Space is required";
                    errorEpaQuestions = true;
                }
                if (quote.epaQuestions.materialSafetyDataSheet < 0) {
                    formIsValid = false;
                    errors["materialSafetyDataSheet"] = "MSDS is required";
                    errorEpaQuestions = true;
                }
                if (quote.epaQuestions.materialSafetyDataSheet === 0 && quote.epaQuestions.sampleTestingRequired < 0) {
                    formIsValid = false;
                    errors["sampleTestingRequired"] = "Sample Testing is required";
                    errorEpaQuestions = true;
                }
            }

            if (quote.epaQuestions.waterAccessOnSite < 0) {
                formIsValid = false;
                errors["waterAccessOnSite"] = "Water Access Onsite is required";
                errorEpaQuestions = true;
            }

            if (canHaveMaint) {
                if (quote.epaQuestions.lastService === "") {
                    formIsValid = false;
                    errors["lastService"] = "Last Service is required";
                    errorEpaQuestions = true;
                }
            }

            if (quote.epaQuestions.weekendJob < 0) {
                formIsValid = false;
                errors["weekendJob"] = "Weekend job is required";
                errorEpaQuestions = true;
            }

            if (quote.epaQuestions.weekendJob === 0 && quote.epaQuestions.emergencySameDayJob < 0) {
                formIsValid = false;
                errors["emergencySameDayJob"] = "Emergency / Same Day job is required";
                errorEpaQuestions = true;
            }
            

            if (errorEpaQuestions) {
                errorItems.push("Epa");
            }
        }

        //other validation
        let otherFields = quote.items.filter(i => i.itemTypeId === 22 || i.itemTypeId === 27 || i.itemTypeId === 28 || i.itemTypeId === 29);
        if (otherFields.length > 0) {
            let rowOtherNumber = 0;
            let otherErrorString = "Please enter the following values: ";
            let errorOth = false;

            for (var i = 0; i < otherFields.length; i++) {
                rowOtherNumber += 1;
                let newOthErrorString = "";

                if (otherFields[i].itemId < 0) {
                    newOthErrorString += " Item, ";
                    errorOth = true;
                }

                //don't schedule jobs for pricelist quotes
                if (this.props.jobTypeId != 3) {
                    if (!otherFields[i].scheduleDate) {
                        newOthErrorString += " Valid Delivery Date, ";
                        errorOth = true;
                    } else if (!DateHelpers.isValidDate(otherFields[i].scheduleDate)) {
                        newOthErrorString += " Valid Delivery Date, ";
                        errorOth = true;
                    }
                    if ((this.props.isEvent || this.props.isThreeDayHire || this.props.quote.schedulePickup) && !otherFields[i].pickupDate && !otherFields[i].installOnly) {
                        newOthErrorString += " Valid Pickup Date, ";
                        errorOth = true;
                    } else if (otherFields[i].pickupDate && otherFields[i].scheduleDate) {
                        if (!DateHelpers.isValidDate(otherFields[i].pickupDate)) {
                            newOthErrorString += " Valid Pickup Date, ";
                            errorOth = true;
                        }
                        else if (!DateHelpers.compareDate(otherFields[i].scheduleDate!, otherFields[i].pickupDate!)) {
                            newOthErrorString += " Pickup Date >= Schedule Date, ";
                            errorOth = true;
                        }
                    }
                }

                if (otherFields[i].hirePeriod <= 0) {
                    newOthErrorString += " Hire Period, ";
                    errorOth = true;
                }

                if (otherFields[i].quantity <= 0) {
                    newOthErrorString += " Quantity, ";
                    errorOth = true;
                }

                var initialHire = otherFields[i].initialHire * 1;
                if (initialHire <= 0) {
                    newOthErrorString += " Initial Hire, ";
                    errorOth = true;
                }

                var amount = otherFields[i].amount * 1;
                if (amount <= 0) {
                    newOthErrorString += " Amount, ";
                    errorOth = true;
                }

                var overhireRate = parseFloat(otherFields[i].overhireRate);
                if (overhireRate <= 0 && otherFields[i].extendable) {
                    newOthErrorString += " Overhire Rate, ";
                    errorOth = true;
                }

                if (newOthErrorString != "") {
                    otherErrorString += "\n"
                    otherErrorString += "Row " + rowOtherNumber + ": " + newOthErrorString;
                    otherErrorString = otherErrorString.substring(0, otherErrorString.length - 2);
                }

            }

            if (errorOth) {
                formIsValid = false;
                errors["otherQuoteItems"] = otherErrorString;
                errorItems.push("Other");
            }
        }

        //event validation
        let eventFields = quote.items.filter(i => i.isEvent);
        if (eventFields.length > 0) {
            let rowEventNumber = 0;
            let eventErrorString = "Please enter the following values: ";
            let errorEv = false;

            for (var i = 0; i < eventFields.length; i++) {
                rowEventNumber += 1;
                let newEventErrorString = "";

                if (eventFields[i].itemId < 0) {
                    newEventErrorString += " Item, ";
                    errorEv = true;
                }

                //don't schedule jobs for pricelist quotes
                if (this.props.jobTypeId != 3) {
                    if (!eventFields[i].scheduleDate) {
                        newEventErrorString += " Valid Delivery Date, ";
                        errorEv = true;
                    } else if (!DateHelpers.isValidDate(eventFields[i].scheduleDate)) {
                        newEventErrorString += " Valid Delivery Date, ";
                        errorEv = true;
                    }
                    if ((this.props.isEvent || this.props.isThreeDayHire || this.props.quote.schedulePickup) && !eventFields[i].pickupDate && !eventFields[i].installOnly) {
                        newEventErrorString += " Valid Pickup Date, ";
                        errorEv = true;
                    } else if (eventFields[i].pickupDate && eventFields[i].scheduleDate) {
                        if (!DateHelpers.isValidDate(eventFields[i].pickupDate)) {
                            newEventErrorString += " Valid Pickup Date, ";
                            errorEv = true;
                        }
                        else if (!DateHelpers.compareDate(eventFields[i].scheduleDate!, eventFields[i].pickupDate!)) {
                            newEventErrorString += " Pickup Date >= Schedule Date, ";
                            errorEv = true;
                        }
                    }
                }

                if (eventFields[i].itemTypeId === 38 && (!eventFields[i].hours || eventFields[i].hours <= 0)) {
                    newEventErrorString += " Hours, ";
                    errorEv = true;
                }

                if (eventFields[i].quantity <= 0) {
                    newEventErrorString += " Quantity, ";
                    errorEv = true;
                }

                var amount = eventFields[i].amount * 1;
                if (amount <= 0) {
                    newEventErrorString += " Amount, ";
                    errorEv = true;
                }

                var pickupScheduled = this.props.isEvent || this.props.isThreeDayHire;
                if (!pickupScheduled) {
                    //if they haven't booked in a pickup then they need to enter an overhire rate
                    var overhireRate = parseFloat(eventFields[i].overhireRate);
                    if (overhireRate <= 0 && eventFields[i].extendable) {
                        newEventErrorString += " Overhire Rate, ";
                        errorEv = true;
                    }
                }

                if (newEventErrorString != "") {
                    eventErrorString += "\n"
                    eventErrorString += "Row " + rowEventNumber + ": " + newEventErrorString;
                    eventErrorString = eventErrorString.substring(0, eventErrorString.length - 2);
                }

            }

            if (errorEv) {
                formIsValid = false;
                errors["eventQuoteItems"] = eventErrorString;
                errorItems.push("Event");
            }
        }

        this.setState({ errors: errors });
        var validForm = {
            valid: formIsValid,
            items: errorItems
        };

        return validForm;
    }

    next = () => {
        var validateForm = this.validate();

        if (validateForm.valid) {
            this.props.goNext();
        } else {
            var errMsg = "Check validation errors before going Next!";
            if (validateForm.items.length > 0) {
                errMsg += " Check Items:";
            }
            for (var i = 0; i < validateForm.items.length; i++) {
                errMsg += "\n" + validateForm.items[i];
            }
            toast.error(errMsg);
        }
    }

    render() {
        let contents = this.state.loading
            ? <p><em>Loading...</em></p>
            : this.renderDetails();

        return <div>
            {contents}
        </div>;
    }

    renderDetails = () => {
        if (this.props.currentStep !== 3) {
            return null;
        }

        let quote = this.props.quote;
        let prepaidNote = "PREPAID CUSTOMER => REMEMBER TO CHARGE CREDIT CARD";
        var contents;
        if (this.state.tab === 'EWS') {
            contents = <NewJobPricelistEwsData isJobEvent={this.props.isEvent || this.props.isThreeDayHire} jobTypeId={this.props.jobTypeId} customerQuoteNotesDunny={this.props.customerQuoteNotesDunny}
                schedulePickup={this.props.quote.schedulePickup} lunchRoomExtraId={this.props.lunchRoomExtraId} siteOfficeExtraId={this.props.siteOfficeExtraId} customerPickup={this.props.quote.customerPickingUp}
                allowAllocateToPastDate={this.props.allowAllocateToPastDate} jobInstalled={this.props.quote.jobInstalled} tiltSlideClearance={quote.tiltSlideClearance} accountTypeId={this.props.accountTypeId}
                quoteItems={quote.items.filter(i => i.isEws || i.itemId < 0)} itemOptions={this.props.itemOptions.filter(i => i.isEws)} truckHireRate={this.props.truckHireRate} fenceQuestions={this.props.quote.fenceQuestions}
                extraItemOptions={this.props.extraItemOptions} yesNoOptions={this.props.yesNoOptions} truckOptions={this.props.truckOptions} truckSideOptions={this.props.truckSideOptions}
                chargeFuelSurchargeSwitchedOn={this.props.chargeFuelSurchargeSwitchedOn} customerNewFuelInvoice={this.props.customerNewFuelInvoice} customerChargeFuelSurcharge={this.props.customerChargeFuelSurcharge}
                errors={this.state.errors} addItem={this.addItem} deleteItem={this.deleteItem} updateQuoteItem={this.updateQuoteItem} updateTiltClearance={this.updateTiltClearance} />
        } else if (this.state.tab === 'CAM') {
            contents = <NewJobPricelistCameraData isJobEvent={this.props.isEvent || this.props.isThreeDayHire} jobTypeId={this.props.jobTypeId} customerQuoteNotesDunny={this.props.customerQuoteNotesDunny}
                schedulePickup={this.props.quote.schedulePickup} allowAllocateToPastDate={this.props.allowAllocateToPastDate} jobInstalled={this.props.quote.jobInstalled} accountTypeId={this.props.accountTypeId} cameraLocation={quote.cameraLocation}
                quoteItems={quote.items.filter(i => i.itemTypeId === 19 || i.itemId < 0)} itemOptions={this.props.itemOptions.filter(i => i.typeId === 19)} truckHireRate={this.props.truckHireRate} fenceQuestions={this.props.quote.fenceQuestions}
                chargeFuelSurchargeSwitchedOn={this.props.chargeFuelSurchargeSwitchedOn} customerNewFuelInvoice={this.props.customerNewFuelInvoice} customerChargeFuelSurcharge={this.props.customerChargeFuelSurcharge}
                errors={this.state.errors} addItem={this.addItem} deleteItem={this.deleteItem} updateQuoteItem={this.updateQuoteItem} updateCameraLocation={this.updateCameraLocation} />
        } else if (this.state.tab === 'PP') {
            contents = <NewJobPricelistPowerPoleData jobTypeId={this.props.jobTypeId} customerQuoteNotesDunny={this.props.customerQuoteNotesDunny}
                allowAllocateToPastDate={this.props.allowAllocateToPastDate} jobInstalled={this.props.quote.jobInstalled} powerPoleQuestions={quote.powerPoleQuestions} yesNoOptions={this.props.yesNoOptions}
                powerPoleDistanceOptions={this.props.powerPoleDistanceOptions} powerPoleLocationOptions={this.props.powerPoleLocationOptions} superSafeSupplierOptions={this.props.superSafeSupplierOptions}
                quoteItems={quote.items.filter(i => i.itemTypeId === 23 || i.itemTypeId === 57 || i.itemId < 0)} itemOptions={this.props.itemOptions.filter(i => i.typeId === 23 || i.typeId === 57)} gateCode={this.props.gateCode} requirePickup={this.props.isEvent || this.props.isThreeDayHire || this.props.quote.schedulePickup}
                chargeFuelSurchargeSwitchedOn={this.props.chargeFuelSurchargeSwitchedOn} customerNewFuelInvoice={this.props.customerNewFuelInvoice} customerChargeFuelSurcharge={this.props.customerChargeFuelSurcharge}
                errors={this.state.errors} addItem={this.addItem} deleteItem={this.deleteItem} updateQuoteItem={this.updateQuoteItem} updatePowerPoleQuestions={this.updatePowerPoleQuestions} updateGateCode={this.props.updateGateCode} />
        } else if (this.state.tab === 'F') {
            var quoteItems = quote.items.filter(i => i.itemTypeId === 8 || (i.itemTypeId >= 50 && i.itemTypeId <= 54) || i.itemId < 0);
            var itemOptions = this.props.itemOptions.filter(i => i.typeId === 8 || (i.typeId >= 50 && i.typeId <= 54));

            contents = <NewJobPricelistFencingData isJobEvent={this.props.isEvent || this.props.isThreeDayHire} jobTypeId={this.props.jobTypeId} accountTypeId={this.props.accountTypeId}
                schedulePickup={this.props.quote.schedulePickup} allowAllocateToPastDate={this.props.allowAllocateToPastDate} jobInstalled={this.props.quote.jobInstalled} truckHireRate={this.props.truckHireRate} fenceQuestions={this.props.quote.fenceQuestions}
                quoteItems={quoteItems} itemOptions={itemOptions} customerQuoteNotesDunny={this.props.customerQuoteNotesDunny} customerInvoiceQuotedAmount={this.props.customerInvoiceQuotedAmount}
                chargeFuelSurchargeSwitchedOn={this.props.chargeFuelSurchargeSwitchedOn} customerNewFuelInvoice={this.props.customerNewFuelInvoice} customerChargeFuelSurcharge={this.props.customerChargeFuelSurcharge}
                errors={this.state.errors} addError={this.addError} clearError={this.clearError} addItem={this.addItem} deleteItem={this.deleteItem} updateFenceQuestions={this.updateFenceQuestions} updateQuoteItem={this.updateQuoteItem} />
        } else if (this.state.tab === 'T') {
            var itemOptions = this.props.itemOptions.filter(i => i.typeId === 25 || i.typeId === 26 || (i.typeId >= 44 && i.typeId <= 47));
            if (this.props.isEvent || this.props.isThreeDayHire) {
                //remove plastic and colourbond toilets from the list
                itemOptions = itemOptions.filter(i => i.id < 74 || i.id > 77);
            }
            var frequency = this.props.frequencyOptions.filter(f => f.id < 4);

            //When updating itemTypeId ALSO MUST update the validation routine
            contents = <NewJobPricelistToiletData isJobEvent={this.props.isEvent || this.props.isThreeDayHire} jobTypeId={this.props.jobTypeId} customerQuoteNotesDunny={this.props.customerQuoteNotesDunny}
                schedulePickup={this.props.quote.schedulePickup} allowAllocateToPastDate={this.props.allowAllocateToPastDate} jobInstalled={this.props.quote.jobInstalled} accountTypeId={this.props.accountTypeId}
                quoteItems={quote.items.filter(i => i.itemTypeId === 25 || i.itemTypeId === 26 || (i.itemTypeId >= 44 && i.itemTypeId <= 47) || i.itemId < 0)} itemOptions={itemOptions} truckHireRate={this.props.truckHireRate} fenceQuestions={this.props.quote.fenceQuestions} frequencyOptions={frequency}
                chargeFuelSurchargeSwitchedOn={this.props.chargeFuelSurchargeSwitchedOn} customerNewFuelInvoice={this.props.customerNewFuelInvoice} customerChargeFuelSurcharge={this.props.customerChargeFuelSurcharge}
                errors={this.state.errors} addItem={this.addItem} deleteItem={this.deleteItem} updateQuoteItem={this.updateQuoteItem} />
        } else if (this.state.tab === 'O') {
            //When updating itemTypeId ALSO MUST update the validation routine
            contents = <NewJobPricelistOtherData isJobEvent={this.props.isEvent || this.props.isThreeDayHire} jobTypeId={this.props.jobTypeId} customerQuoteNotesDunny={this.props.customerQuoteNotesDunny}
                schedulePickup={this.props.quote.schedulePickup} allowAllocateToPastDate={this.props.allowAllocateToPastDate} jobInstalled={this.props.quote.jobInstalled} accountTypeId={this.props.accountTypeId}
                quoteItems={quote.items.filter(i => i.itemTypeId === 22 || i.itemTypeId === 27 || i.itemTypeId === 28 || i.itemTypeId === 29 || i.itemId < 0)} itemOptions={this.props.itemOptions.filter(i => i.typeId === 22 || i.typeId === 27 || i.typeId === 28 || i.typeId === 29)}
                truckHireRate={this.props.truckHireRate} fenceQuestions={this.props.quote.fenceQuestions}
                chargeFuelSurchargeSwitchedOn={this.props.chargeFuelSurchargeSwitchedOn} customerNewFuelInvoice={this.props.customerNewFuelInvoice} customerChargeFuelSurcharge={this.props.customerChargeFuelSurcharge}
                errors={this.state.errors} addItem={this.addItem} deleteItem={this.deleteItem} updateQuoteItem={this.updateQuoteItem} />
        } else if (this.state.tab === 'E') {
            contents = <NewJobPricelistEventData jobTypeId={this.props.jobTypeId} customerQuoteNotesDunny={this.props.customerQuoteNotesDunny}
                schedulePickup={this.props.quote.schedulePickup} allowAllocateToPastDate={this.props.allowAllocateToPastDate} jobInstalled={this.props.quote.jobInstalled} isEventJob={this.props.isEvent || this.props.isThreeDayHire} priceOnly={this.props.priceOnly}
                quoteItems={quote.items.filter(i => (i.itemTypeId >= 31 && i.itemTypeId <= 40) || (i.itemTypeId >= 42 && i.itemTypeId <= 43) || i.itemTypeId === 48 || i.itemTypeId === 55 || i.itemId < 0)} itemOptions={this.props.itemOptions.filter(i => (i.typeId >= 31 && i.typeId <= 40) || (i.typeId >= 42 && i.typeId <= 43) || i.typeId === 48 || i.typeId === 55)}
                errors={this.state.errors} addItem={this.addItem} deleteItem={this.deleteItem} updateQuoteItem={this.updateQuoteItem} />
        } else if (this.state.tab === 'EPA') {
            contents = <NewJobPricelistEpaData isJobEvent={this.props.isEvent || this.props.isThreeDayHire} customerId={this.props.customerId} jobTypeId={this.props.jobTypeId} customerQuoteNotesDunny={this.props.customerQuoteNotesDunny} 
                schedulePickup={this.props.quote.schedulePickup} allowAllocateToPastDate={this.props.allowAllocateToPastDate} jobInstalled={this.props.quote.jobInstalled} accountTypeId={this.props.accountTypeId} truckHireRate={this.props.truckHireRate} fenceQuestions={this.props.quote.fenceQuestions} epaQuestions={this.props.quote.epaQuestions}
                frequencyOptions={this.props.frequencyOptions} yesNoOptions={this.props.yesNoOptions} epaExtra30MinOnSiteFeeFormatted={this.props.epaExtra30MinOnSiteFeeFormatted} emergencyFeeFormatted={this.props.emergencyFeeFormatted} weekendFeeFormatted={this.props.weekendFeeFormatted} quoteItems={quote.items.filter(i => (i.itemTypeId >= 58 && i.itemTypeId <= 61) || i.itemId < 0)} itemOptions={this.props.itemOptions.filter(i => i.typeId >= 58 && i.typeId <= 61)}
                chargeFuelSurchargeSwitchedOn={this.props.chargeFuelSurchargeSwitchedOn} customerNewFuelInvoice={this.props.customerNewFuelInvoice} customerChargeFuelSurcharge={this.props.customerChargeFuelSurcharge}
                errors={this.state.errors} clearError={this.clearError} addItem={this.addItem} deleteItem={this.deleteItem} updateQuoteItem={this.updateQuoteItem} updateEpaQuestions={this.updateEpaQuestions} />
        }

        return (
            <div>
                <div>
                    <div className="tabsComponent" id="tab-container">
                        <div className={this.state.isRabPthPricelist ? "newJobPricelist--customer-notes" : "hidden"}>RAB / PTH Pricelist Customer</div>
                        <div className="columngroup">
                            <div className="columnleft">
                                <div>
                                    <label className="input-group marginLeft10" htmlFor="customerPickingUp">
                                        <span className="label">Is customer picking up?</span>
                                        <select className="select-small" id="customerPickingUp" name="customerPickingUp" value={quote.customerPickingUp} onChange={(e) => this.handleCustomerPickupChange(e)}>
                                            <option hidden defaultValue="-1"></option>
                                            {this.props.yesNoOptions.map(yn =>
                                                <option key={yn.id} value={yn.id}>{yn.name}</option>
                                            )};
                                        </select>
                                    </label>
                                    <span className={this.state.errors["customerPickingUp"] ? "label errors" : "hidden"}>{this.state.errors["customerPickingUp"]}</span>

                                    <div className={this.props.jobTypeId === 3 ? "hidden" : ""}>
                                        <label className="input-group--inline marginLeft10" htmlFor="jobInstalled">
                                            <span className="label">Job Already Installed on Site?</span>
                                            <input className="checkbox" type="checkbox" name="jobInstalled" checked={quote.jobInstalled} onChange={(e) => this.handleCheckboxChange(e)} ></input>
                                        </label>

                                        <label className={quote.jobInstalled ? "input-group--inline marginLeft10" : "hidden"} htmlFor="jobInvoiced">
                                            <span className="label">Job Invoiced?</span>
                                            <input className="checkbox" type="checkbox" name="jobInvoiced" checked={quote.jobInvoiced} onChange={(e) => this.handleCheckboxChange(e)} ></input>
                                        </label>
                                        <label className={quote.jobInvoiced ? "input-group--inline marginLeft10" : "hidden"} htmlFor="jobInvoicedDate">
                                            <span className="label">Invoiced Up To</span>
                                            <input className="input" type="date" name="jobInvoicedDate" value={quote.jobInvoicedDate ?? ""} onChange={(e) => this.handleChange(e)} ></input>
                                        </label>

                                        <label className="input-group marginLeft10" htmlFor="schedulePickup">
                                            <span className="label">Schedule Pickup?</span>
                                            <input className="checkbox" type="checkbox" name="schedulePickup" checked={quote.schedulePickup} onChange={(e) => this.handleCheckboxChange(e)} ></input>
                                        </label>
                                    </div>
                                </div>

                                <div className={this.props.accountTypeId === 2 || this.props.accountTypeId === 3 || this.props.jobTypeId === 3 ? "hidden" : ""}>
                                    <div>
                                        <label className="input-group marginLeft10" htmlFor="payType">
                                            <span className="label">Payment Type</span>
                                            <input type="radio" className="marginRight10" name="paymentType" id="creditcard" value={3} checked={quote.paymentType === 3} onChange={(e) => this.handleRadioChange(e)}></input>
                                            <label htmlFor="creditcard" className="marginRight10 marginBottomNone">Credit Card</label>
                                            <input type="radio" className="marginRight10" name="paymentType" id="directdebit" value={4} checked={quote.paymentType === 4} onChange={(e) => this.handleRadioChange(e)}></input>
                                            <label htmlFor="directdebit" className="marginRight10 marginBottomNone">Direct Debit</label>
                                        </label>
                                        <span className={this.state.errors["paymentType"] ? "label errors" : "hidden"}>{this.state.errors["paymentType"]}</span>
                                    </div>

                                    <div className={quote.paymentType < 0 || quote.paymentType === 3 ? "newJobPricelist--prepaid" : "hidden"}>{prepaidNote}</div>
                                </div>
                            </div>
                            <div className="columnright">
                                <label className="input-group" htmlFor="notes">
                                    <span className="label">Notes</span>
                                    <textarea className="input notes__pricelistSize" id="notes" name="notes" value={this.props.notes} onChange={(e) => this.props.updateNotes(e.target.value)}></textarea>
                                </label>
                            </div>
                        </div>

                        <span className={this.state.errors["quoteItems"] ? "label errors" : "hidden"}>{this.state.errors["quoteItems"]}</span>
                        <div className="tabsComponent__showDropdown">
                            <label className="input-group" htmlFor="jobTab">
                                <span className="label">Item Type</span>
                                <select className="select" id="jobTab" value={this.state.tab} onChange={(e) => this.tabSelected(e.target.value)} >
                                    <option value="T">Toilets</option>
                                    <option value="F">Fencing</option>
                                    <option value="EPA">EPA</option>
                                    <option value="PP">Power Poles</option>
                                    <option value="EWS">EWS/Site</option>
                                    <option value="CAM">Cameras</option>
                                    <option value="O">Other</option>
                                    <option value="E">Event</option>
                                </select>
                            </label>
                        </div>

                        <div className="tabsComponent__tabs tabsComponent__hideTabs">
                            <input type="radio" name="subtabs" id="subtab4" className="tabsComponent__control" checked={this.state.tab == 'T'} onChange={(e) => this.noChange(e)}></input>
                            <label className="tabsComponent__label newJobPricelist--tabsize" htmlFor="tabItem" onClick={() => this.tabSelected('T')}>Toilets</label>
                            <input type="radio" name="subtabs" id="subtab4" className="tabsComponent__control" checked={this.state.tab == 'F'} onChange={(e) => this.noChange(e)}></input>
                            <label className="tabsComponent__label newJobPricelist--tabsize" htmlFor="tabItem" onClick={() => this.tabSelected('F')}>Fencing</label>
                            <input type="radio" name="subtabs" id="subtab4" className="tabsComponent__control" checked={this.state.tab == 'EPA'} onChange={(e) => this.noChange(e)}></input>
                            <label className="tabsComponent__label newJobPricelist--tabsize" htmlFor="tabItem" onClick={() => this.tabSelected('EPA')}>EPA</label>
                            <input type="radio" name="subtabs" id="subtab3" className="tabsComponent__control" checked={this.state.tab == 'PP'} onChange={(e) => this.noChange(e)}></input>
                            <label className="tabsComponent__label newJobPricelist--tabsize" htmlFor="tabItem" onClick={() => this.tabSelected('PP')}>Power Poles</label>
                            <input type="radio" name="subtabs" id="subtab1" className="tabsComponent__control" checked={this.state.tab == 'EWS'} onChange={(e) => this.noChange(e)}></input>
                            <label className="tabsComponent__label newJobPricelist--tabsize" htmlFor="tabItem" onClick={() => this.tabSelected('EWS')}>EWS / SITE BUILDING</label>
                            <input type="radio" name="subtabs" id="subtab2" className="tabsComponent__control" checked={this.state.tab == 'CAM'} onChange={(e) => this.noChange(e)}></input>
                            <label className="tabsComponent__label newJobPricelist--tabsize" htmlFor="tabItem" onClick={() => this.tabSelected('CAM')}>Cameras</label>
                            <input type="radio" name="subtabs" id="subtab5" className="tabsComponent__control" checked={this.state.tab == 'O'} onChange={(e) => this.noChange(e)}></input>
                            <label className="tabsComponent__label newJobPricelist--tabsize" htmlFor="tabItem" onClick={() => this.tabSelected('O')}>Other</label>
                            <input type="radio" name="subtabs" id="subtab6" className="tabsComponent__control" checked={this.state.tab == 'E'} onChange={(e) => this.noChange(e)}></input>
                            <label className="tabsComponent__label newJobPricelist--tabsize" htmlFor="tabItem" onClick={() => this.tabSelected('E')}>Event</label>
                        </div>

                        <div className="tabsComponent__content tabsComponent__content--selected">
                            {contents}
                        </div>

                        <div className="makeitflexspacebetween">
                            <div>
                                <div>Ask Customer to send through any plans / relevant files</div>
                                <div>If no plans, enter unit location in notes for each unit</div>
                                <div className="marginTop10 alignitemscentre">
                                    <span className="marginRight10">Upload Plans/Files</span> <input type="file" name="file" accept="application/pdf" onChange={this.handleImportFile} multiple></input>
                                </div>
                                <div className="alignitemscentre">
                                    <div className={this.props.files.length > 0 ? "" : "hidden"}>
                                        {this.props.files.map((plan, index) =>
                                            <div key={index}>
                                                <span>{plan.name}</span>
                                                <button type="button" className="marginLeft10 close makeitred" onClick={() => this.deleteFile(index)} >&times;</button>
                                            </div>
                                        )}

                                    </div>
                                </div>
                            </div>
                            <div className="input-group--inline job__quote--total--nomargin">
                                <span className="label-large job__quote--total">Total Amount Quoted</span>
                                <span className="job__quote--amount makeitflexright">{this.props.quote.totalFormatted}</span>
                            </div>
                        </div>
                        <div>
                            <div className="input-group-parent">
                                <button className="defaultbutton" onClick={this.prev} type="button">Prev</button>
                            </div>
                            <div className="input-group-parent">
                                <div className="colAlignRight">
                                    <button className={this.props.priceOnly ? "hidden" : "defaultbutton defaultbutton--save"} onClick={this.next} type="button">Next</button>
                                </div>
                            </div>
                        </div>
                        <div className={this.state.customPrices.length === 0 ? "hidden" : "marginTop10"}>
                            <a href="#" onClick={(e) => this.handleCustomPricesChange(e)}>{this.state.showCustomPrices ? "Hide Custom Prices" : "Show Custom Prices"}</a>

                            <div className={this.state.showCustomPrices ? "overflowAuto" : "hidden"}>
                            <div className="makeitred">NOTE: You MUST match the Item and Hire Period below EXACTLY in order to get the custom price</div>
                                <table className="table">
                                    <thead>
                                        <tr>
                                            <th>Item</th>
                                            <th>Maintenance</th>
                                            <th>Hire Period</th>
                                            <th>Price</th>
                                            <th>Travel One Way Rate</th>
                                            <th>Additional Weekly Rate</th>
                                        </tr>
                                    </thead>
                                    {this.state.customPrices.map((item, index) =>
                                        <tbody key={index}>
                                            <tr>
                                                <td className="table__text--align">{item.item}</td>
                                                <td className="table__text--align">{item.maintenance} {item.metresMin === 0 && item.metresMax === 0 ? "" : item.metres}</td>
                                                <td className="table__text--align">{item.hirePeriod}</td>
                                                <td className="table__text--align">
                                                    {item.priceFormatted}
                                                    {item.metreRateFormatted}
                                                </td>
                                                <td className="table__text--align">{item.travelOneWayRateFormatted}</td>
                                                <td className="table__text--align">
                                                    {item.baseAdditionalHireRateFormatted}
                                                    {item.additionalHireRateFormatted}
                                                </td>
                                            </tr>
                                        </tbody>
                                    )}
                                </table>
                            </div>
                        </div>
                    </div>

                </div>
            </div>
        );

    }
}